
import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const ImageSequence = () => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [images, setImages] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);

    // Configuration
    const frameCount = 84;           // Nombre total de frames pour le scrub GSAP (inchangé)
    const frameStep = 2;             // On ne charge qu'une image sur deux
    const loadedFrameCount = Math.ceil(frameCount / frameStep); // 42 images réelles
    const imagePath = (index) => `${process.env.PUBLIC_URL}/assets/animation_house/maison000${86400 + index}.webp`;


    // Preload images — uniquement les frames paires (0, 2, 4, ... 82)
    useEffect(() => {
        const loadImages = async () => {
            const promises = [];

            for (let i = 0; i < loadedFrameCount; i++) {
                const realFrame = i * frameStep; // 0, 2, 4, …, 82
                const img = new Image();
                img.src = imagePath(realFrame);
                promises.push(
                    new Promise((resolve) => {
                        img.onload = () => resolve(img);
                        img.onerror = () => {
                            console.warn(`Failed to load image: ${img.src}`);
                            resolve(null);
                        };
                    })
                );
            }

            const results = await Promise.all(promises);
            setImages(results);
            setIsLoaded(true);
        };

        loadImages();
    }, []);

    // Animation Logic
    useEffect(() => {
        if (!isLoaded || images.length === 0) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');

        // Keep track of the ScrollTrigger instance safely
        let scrollTriggerInstance;
        let resizeTimeout = null;
        let lastWidth = 0;
        let lastHeight = 0;

        const dpr = window.devicePixelRatio || 1;

        // Initial render
        const render = (index) => {
            if (index >= 0 && index < images.length) {
                const img = images[index];
                if (img && img.complete && img.naturalWidth > 0) {
                    const logicalWidth = canvas.width / dpr;
                    const logicalHeight = canvas.height / dpr;

                    const scale = Math.max(logicalWidth / img.width, logicalHeight / img.height) * 0.4;
                    const x = (logicalWidth - img.width * scale) / 2;
                    const y = (logicalHeight / 2) - (img.height / 2) * scale - 50;

                    context.clearRect(0, 0, logicalWidth, logicalHeight);
                    context.drawImage(img, x, y, img.width * scale, img.height * scale);
                }
            }
        };

        // Applique les nouvelles dimensions au canvas et redessine le frame courant
        const applyResize = () => {
            // clientWidth/clientHeight est plus stable que innerHeight sur mobile
            // (ignorer les changements dus à la barre d'adresse du navigateur)
            const logicalWidth = document.documentElement.clientWidth;
            const logicalHeight = document.documentElement.clientHeight;

            // Ne rien faire si les dimensions n'ont pas changé de plus de 5px
            if (Math.abs(logicalWidth - lastWidth) < 5 && Math.abs(logicalHeight - lastHeight) < 5) return;
            lastWidth = logicalWidth;
            lastHeight = logicalHeight;

            canvas.width = logicalWidth * dpr;
            canvas.height = logicalHeight * dpr;

            context.setTransform(1, 0, 0, 1, 0, 0);
            context.scale(dpr, dpr);
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';

            // Re-render frame courant après resize
            const progress = scrollTriggerInstance ? scrollTriggerInstance.progress : 0;
            const gsapFrame = Math.min(frameCount - 1, Math.floor(progress * (frameCount - 1)));
            render(Math.floor(gsapFrame / frameStep));
        };

        // Debounce : attend 150ms de calme avant de redimensionner
        const handleResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(applyResize, 150);
        };

        // Initialisation immédiate (sans debounce)
        applyResize();
        window.addEventListener('resize', handleResize);

        const animationState = { frame: 0 };

        // Create ScrollTrigger
        scrollTriggerInstance = ScrollTrigger.create({
            trigger: ".hero-sequence-wrapper",
            start: "top top",
            end: "bottom bottom",
            scrub: true,
            onUpdate: (self) => {
                let progress = Math.min(1, self.progress / 0.75);

                if (progress < 0.01) progress = 0;
                if (progress > 0.99) progress = 1;

                // Mapping GSAP (0-83) → tableau réduit (0-41)
                const gsapFrame = Math.min(
                    frameCount - 1,
                    Math.floor(progress * (frameCount - 1))
                );
                const frameIndex = Math.floor(gsapFrame / frameStep);

                if (animationState.frame !== frameIndex) {
                    animationState.frame = frameIndex;
                    render(frameIndex);
                }
            }
        });

        return () => {
            clearTimeout(resizeTimeout);
            window.removeEventListener('resize', handleResize);
            if (scrollTriggerInstance) scrollTriggerInstance.kill();
        };
    }, [isLoaded, images]);


    return (
        <div
            ref={containerRef}
            className="image-sequence-container"
            style={{
                width: '100%',
                height: '100vh',
                overflow: 'hidden',
                // Position handled by CSS
            }}
        >
            {!isLoaded && (
                <div className="sequence-loader-container">
                    <div className="sequence-loader"></div>
                </div>
            )}
            <canvas
                ref={canvasRef}
                className="image-sequence-canvas"
                style={{
                    width: '100%',
                    height: '100%',
                    display: 'block',
                    opacity: isLoaded ? 1 : 0,
                    transition: 'opacity 0.5s ease'
                }}
            />
        </div>
    );
};

export default ImageSequence;
